import React, {createContext, useContext, useState, PropsWithChildren, useEffect} from 'react';
import {jwtDecode} from 'jwt-decode';

interface AuthContextType {
    isAuthenticated: boolean;
    isAdmin: boolean;
    login: (token: string) => void;
    logout: () => void;
}

interface JwtPayload {
    exp: number;
    isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren) {
    const authToken = localStorage.getItem('authToken');
    const [isAuthenticated, setIsAuthenticated] = useState(!!authToken || false);
    const [isAdmin, setIsAdmin] = useState((authToken && jwtDecode<JwtPayload>(authToken)?.isAdmin) || false);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            try {
                const decodedToken = jwtDecode<JwtPayload>(token);
                if (decodedToken.exp * 1000 > Date.now()) {
                    setIsAuthenticated(true);
                    setIsAdmin(decodedToken.isAdmin || false);
                } else {
                    setIsAuthenticated(false);
                    setIsAdmin(false);
                    localStorage.removeItem('authToken');
                }
            } catch (error) {
                setIsAuthenticated(false);
                setIsAdmin(false);
                localStorage.removeItem('authToken');
            }
        }
    }, []);

    const login = (token: string) => {
        localStorage.setItem('authToken', token);
        const decodedToken = jwtDecode<JwtPayload>(token);
        setIsAuthenticated(true);
        setIsAdmin(decodedToken.isAdmin || false);
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        setIsAuthenticated(false);
        setIsAdmin(false);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, isAdmin, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
